<template>
  <DropdownButton
    ref="dropdownButtonRef"
    :title="title"
    :size="buttonSize"
    :type="buttonType"
    @keydown="onDropdownKeydown"
    @closeDropdown="resetFocusedOption"
  >
    <template #dropdown>
      <div
        v-for="optionGroup in groupedOptions"
        :key="JSON.stringify(optionGroup)"
        :class="{ 'action-sheet__dropdown-group_divided': optionGroup.hasDivider }"
      >
        <p
          v-if="optionGroup.label"
          class="action-sheet__dropdown-group-label"
        >
          {{ optionGroup.label }}
        </p>
        <ActionSheetItem
          v-for="option in optionGroup.options"
          ref="actionSheetItemRefs"
          :key="option.value"
          :class="{ 'action-sheet__option-item_focused': option.key === String(focusedOptionIndex) }"
          :label="option.label"
          :value="option.value"
          :subTitle="option.subTitle"
          :type="option.type"
          :withToggle="option.withToggle"
          :icon="option.icon"
          :badge="option.badge"
          :disabled="option.disabled"
          :parentItemOptions="option.parentItemOptions"
          :isFullLabelVisible="isFullLabelVisible"
          :action="option.action"
          @click="onClick"
          @change="onChange"
        />
      </div>
    </template>
  </DropdownButton>
</template>

<script lang="ts">

import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';

import DropdownButton from '@/ui/experimental/DropdownButton/index.vue';
import ActionSheetItem from '@/ui/ActionSheet/components/ActionSheetItem/index.vue';
import {
  EActionSheetType,
  EButtonType,
  ESize,
  TActionSheetOption,
  TActionSheetOptions,
  TActionSheetOptionsGroup,
  TRawActionSheetValue,
} from '@/ui/types';
import { EKeyboardKey } from '@/constants';
import { isActionSheetOptionsGroupsGuard } from '@/ui/ActionSheet/utils/guards';

import { getGroupedOptions } from '../utils/getGroupedOptions';

export default defineComponent({
  name: 'ActionSheet',
  components: {
    DropdownButton,
    ActionSheetItem,
  },
  props: {
    options: {
      type: Array as PropType<TActionSheetOptions>,
      default: () => [],
    },
    isFullLabelVisible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    buttonSize: {
      type: String as PropType<ESize>,
      default: ESize.medium,
    },
    buttonType: {
      type: String as PropType<EButtonType>,
      default: EButtonType.secondary,
    },
  },
  emits: ['click', 'change'],
  setup(props, { emit }) {
    const dropdownButtonRef = ref<typeof DropdownButton | null>(null);
    const optionsLength = ref<number | null>(null);
    const focusedOptionIndex = ref<number | null>(null);
    const actionSheetItemRefs = ref<Array<typeof ActionSheetItem> | null>(null);

    // Вычисляем опции единым списком, если они были в сгруппированном виде, для упрощения поиска selectedValue
    const listedOptions = computed<TActionSheetOption[]>(() => (isActionSheetOptionsGroupsGuard(props.options)
      ? props.options.map((option: TActionSheetOptionsGroup) => option.options).flat()
      : props.options));

    const groupedOptions = computed(() => getGroupedOptions(props.options) as TActionSheetOptionsGroup[]);

    const onChange = (toggleValue: boolean, value: TRawActionSheetValue) => {
      emit('change', toggleValue, value);
    };

    const onClick = (action: () => void) => {
      dropdownButtonRef.value?.handleDropdownToggle();
      emit('click', action);
    };

    const handleKeydownEnter = (option: TActionSheetOption, focusedOptionIndex: number) => {
      if (option.type === EActionSheetType.parentItem && actionSheetItemRefs.value) {
        actionSheetItemRefs.value[focusedOptionIndex].changeSubMenuVisibility();
      } else {
        onClick(option.action);
      }
    };

    const resetFocusedOption = (dropdownVisible: boolean) => {
      if (!dropdownVisible) {
        focusedOptionIndex.value = null;
      }
    };

    const onDropdownKeydown = (event: KeyboardEvent) => {
      event.preventDefault();
      if (!optionsLength.value) return;
      if (event.key === EKeyboardKey.arrowDown) {
        // если фокуса не было, устанавливаем его на первый элемент
        if (focusedOptionIndex.value === null) {
          focusedOptionIndex.value = 0;
        } else if (focusedOptionIndex.value < optionsLength.value - 1) {
          focusedOptionIndex.value += 1;
        }
      }
      if (event.key === EKeyboardKey.arrowUp) {
        // если фокус на 1 элементе или его вообще не было, то ставим на последний элемент списка
        if (focusedOptionIndex.value === 0 || focusedOptionIndex.value === null) {
          focusedOptionIndex.value = optionsLength.value - 1;
        } else if (focusedOptionIndex.value > 0) {
          focusedOptionIndex.value -= 1;
        }
      }
      if (event.key === EKeyboardKey.enter) {
        if (focusedOptionIndex.value === null) return;
        const focusedOption = listedOptions.value[focusedOptionIndex.value];
        if (!focusedOption || focusedOption.disabled) return;
        handleKeydownEnter(focusedOption, focusedOptionIndex.value);
      }
    };

    watch(groupedOptions, () => {
      // для динамически подгружаемых options
      optionsLength.value = groupedOptions.value?.map(
        (option: TActionSheetOptionsGroup) => option.options).flat().length;
    }, { immediate: true });

    watch(focusedOptionIndex, (newIndex: number | null, oldIndex: number | null) => {
      if (!newIndex || !oldIndex || !focusedOptionIndex.value) return;
      if (listedOptions.value[newIndex].disabled) {
        // если находимся вначале списка и идем вверх в конец списка
        if (oldIndex === 0 && newIndex === listedOptions.value.length - 1) {
          focusedOptionIndex.value -= 1;
        } else if (oldIndex > newIndex) { // если идем вверх
          focusedOptionIndex.value -= 1;
        } else { // если идем вниз
          focusedOptionIndex.value += 1;
        }
      }
    });

    return {
      actionSheetItemRefs,
      dropdownButtonRef,
      focusedOptionIndex,
      groupedOptions,
      optionsLength,

      resetFocusedOption,
      onChange,
      onClick,
      onDropdownKeydown,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
